import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  ArrowLeft,
  Calendar,
  Clock,
  Loader2,
  Pencil,
  Save,
  ShieldCheck,
  Users,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { AppLayout } from "@/components/layout/AppLayout";
import { ActivityHistoryDrawer } from "@/components/audit/ActivityHistoryDrawer";
import { formatDateTime } from "@/lib/utils";
import { useRole, useUpdateRole } from "../hooks/useRoles";
import { PermissionsMatrix } from "../components/PermissionsMatrix";

type Tab = "details" | "permissions";

const SUPER_ADMIN = "Super Admin";

function TypeBadge({ isSystem }: { isSystem: boolean }) {
  return (
    <span
      className="inline-flex items-center rounded-full px-2.5 py-0.5 text-[11px] font-medium"
      style={
        isSystem
          ? { background: "hsl(210,55%,92%)", color: "hsl(210,100%,28%)" }
          : { background: "hsl(262,55%,92%)", color: "hsl(262,83%,38%)" }
      }
    >
      {isSystem ? "System" : "Custom"}
    </span>
  );
}

function StatusBadge({ active }: { active: boolean }) {
  return (
    <span
      className="inline-flex items-center rounded-full px-2.5 py-0.5 text-[11px] font-medium"
      style={
        active
          ? { background: "hsl(142,55%,92%)", color: "hsl(142,72%,28%)" }
          : { background: "hsl(210,16%,93%)", color: "hsl(215,16%,40%)" }
      }
    >
      {active ? "Active" : "Inactive"}
    </span>
  );
}

export default function RoleDetailPage() {
  const { id = "" } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { data: role, isLoading, isError } = useRole(id);
  const updateRole = useUpdateRole(id);

  const [tab, setTab] = useState<Tab>("details");
  const [editMode, setEditMode] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [isActive, setIsActive] = useState(true);

  useEffect(() => {
    if (role) {
      setName(role.name);
      setDescription(role.description ?? "");
      setIsActive(role.isActive);
    }
  }, [role]);

  if (isLoading) {
    return (
      <AppLayout>
        <div className="flex min-h-[60vh] items-center justify-center">
          <Loader2 className="mr-2 h-4 w-4 animate-spin" strokeWidth={1.5} />
          <span className="text-sm text-muted-foreground">Loading role…</span>
        </div>
      </AppLayout>
    );
  }

  if (isError || !role) {
    return (
      <AppLayout>
        <div className="flex min-h-[60vh] items-center justify-center text-sm text-destructive">
          Failed to load role.
        </div>
      </AppLayout>
    );
  }

  const isSuperAdmin = role.isSystem && role.name === SUPER_ADMIN;

  async function handleSave() {
    if (!role) return;
    await updateRole.mutateAsync({
      name: role.isSystem ? undefined : name,
      description,
      isActive: role.isSystem ? undefined : isActive,
    });
    setEditMode(false);
  }

  return (
    <AppLayout
      breadcrumb={[
        { label: "PhazeX Hub", href: "/" },
        { label: "Administration" },
        { label: "Role Management", href: "/admin/roles" },
        { label: role.name },
      ]}
    >
      <div className="w-full space-y-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="icon" onClick={() => navigate("/admin/roles")}>
              <ArrowLeft className="h-4 w-4" strokeWidth={1.5} />
            </Button>
            <ShieldCheck className="h-[18px] w-[18px]" strokeWidth={1.5} />
            <h1 className="text-2xl font-semibold tracking-tight">View Role</h1>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="icon"
              aria-label="Edit"
              onClick={() => setEditMode((v) => !v)}
            >
              <Pencil className="h-4 w-4" strokeWidth={1.5} />
            </Button>
            <Button
              variant="outline"
              size="icon"
              aria-label="History"
              onClick={() => setHistoryOpen(true)}
            >
              <Clock className="h-4 w-4" strokeWidth={1.5} />
            </Button>
          </div>
        </div>

        <Card className="w-full">
          <CardContent className="pt-6">
            <div className="flex flex-wrap items-center gap-4">
              <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-[hsl(210,55%,92%)] text-[hsl(210,100%,28%)]">
                <ShieldCheck className="h-6 w-6" strokeWidth={1.5} />
              </div>
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-2.5">
                  <h2
                    className="font-bold tracking-tight"
                    style={{ fontSize: 20, fontWeight: 700 }}
                  >
                    {role.name}
                  </h2>
                  <TypeBadge isSystem={role.isSystem} />
                  <StatusBadge active={role.isActive} />
                </div>
                {role.description && (
                  <p className="mt-1 text-sm text-muted-foreground">
                    {role.description}
                  </p>
                )}
              </div>
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Users className="h-4 w-4" strokeWidth={1.5} />
                <span className="font-medium text-foreground">{role.userCount}</span>
                <span>{role.userCount === 1 ? "user" : "users"}</span>
              </div>
            </div>
          </CardContent>
        </Card>

        <div className="border-b border-border">
          <nav className="-mb-px flex gap-6">
            <button
              type="button"
              onClick={() => setTab("details")}
              className={`border-b-2 px-1 pb-3 text-sm font-medium transition-colors ${
                tab === "details"
                  ? "border-primary text-primary"
                  : "border-transparent text-muted-foreground hover:text-foreground"
              }`}
            >
              Details
            </button>
            <button
              type="button"
              onClick={() => setTab("permissions")}
              className={`border-b-2 px-1 pb-3 text-sm font-medium transition-colors ${
                tab === "permissions"
                  ? "border-primary text-primary"
                  : "border-transparent text-muted-foreground hover:text-foreground"
              }`}
            >
              Permissions Matrix
            </button>
          </nav>
        </div>

        {tab === "details" ? (
          <div className="grid w-full grid-cols-1 gap-6 lg:grid-cols-10">
            <Card className="lg:col-span-7">
              <CardContent className="space-y-5 pt-6">
                <h3 className="text-sm font-semibold text-foreground">Basic Info</h3>

                <div className="space-y-2">
                  <Label htmlFor="role-name">Role Name</Label>
                  <Input
                    id="role-name"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    disabled={!editMode || role.isSystem}
                  />
                  {role.isSystem && (
                    <p className="text-[11px] text-muted-foreground">
                      System role names cannot be changed.
                    </p>
                  )}
                </div>

                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <div className="space-y-2">
                    <Label>Role Type</Label>
                    <div>
                      <TypeBadge isSystem={role.isSystem} />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="role-active">Status</Label>
                    <div className="flex items-center gap-2">
                      <input
                        id="role-active"
                        type="checkbox"
                        checked={isActive}
                        disabled={!editMode || role.isSystem}
                        onChange={(e) => setIsActive(e.target.checked)}
                        className="h-4 w-4 rounded border-border"
                      />
                      <StatusBadge active={isActive} />
                    </div>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="role-description">Description</Label>
                  <textarea
                    id="role-description"
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    disabled={!editMode}
                    placeholder="Short description of this role"
                    rows={4}
                    className="flex w-full rounded-md border border-border bg-background px-3 py-2 text-sm shadow-sm transition-colors placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1 disabled:cursor-not-allowed disabled:opacity-50"
                  />
                </div>

                {editMode && (
                  <div className="flex justify-end gap-2 pt-2">
                    <Button
                      variant="outline"
                      onClick={() => {
                        setEditMode(false);
                        setName(role.name);
                        setDescription(role.description ?? "");
                        setIsActive(role.isActive);
                      }}
                    >
                      Cancel
                    </Button>
                    <Button onClick={handleSave} disabled={updateRole.isPending}>
                      {updateRole.isPending ? (
                        <Loader2 className="h-4 w-4 animate-spin" strokeWidth={1.5} />
                      ) : (
                        <Save className="h-4 w-4" strokeWidth={1.5} />
                      )}
                      Save
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>

            <div className="space-y-6 lg:col-span-3">
              <Card>
                <CardContent className="space-y-4 pt-6">
                  <h3 className="text-sm font-semibold text-foreground">Dates</h3>
                  <div className="space-y-3 text-sm">
                    <div className="flex items-start gap-2">
                      <Calendar
                        className="mt-0.5 h-4 w-4 text-muted-foreground"
                        strokeWidth={1.5}
                      />
                      <div>
                        <div className="text-[11px] uppercase tracking-wide text-muted-foreground">
                          Created At
                        </div>
                        <div className="text-foreground">
                          {formatDateTime(role.createdAt)}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-start gap-2">
                      <Clock
                        className="mt-0.5 h-4 w-4 text-muted-foreground"
                        strokeWidth={1.5}
                      />
                      <div>
                        <div className="text-[11px] uppercase tracking-wide text-muted-foreground">
                          Updated At
                        </div>
                        <div className="text-foreground">
                          {formatDateTime(role.updatedAt)}
                        </div>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="pt-6">
                  <div className="flex flex-col items-center justify-center py-2 text-center">
                    <div className="flex h-10 w-10 items-center justify-center rounded-full bg-[hsl(210,55%,92%)] text-[hsl(210,100%,28%)]">
                      <Users className="h-5 w-5" strokeWidth={1.5} />
                    </div>
                    <div className="mt-2 text-3xl font-bold text-foreground">
                      {role.userCount}
                    </div>
                    <div className="text-xs text-muted-foreground">
                      users assigned to this role
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        ) : (
          <PermissionsMatrix
            roleId={role.id}
            roleName={role.name}
            isReadOnly={isSuperAdmin}
          />
        )}

        <ActivityHistoryDrawer
          isOpen={historyOpen}
          onClose={() => setHistoryOpen(false)}
          entityType="role"
          entityId={role.id}
          entityLabel={role.name}
        />
      </div>
    </AppLayout>
  );
}
